import { AskOracleInput } from '@modules/oracle/components/AskOracleInput';
import { AskOracleInputActionsHandle } from '@modules/oracle/components/AskOracleInput/AskOracleInput';
import { useGetPromptPosition } from '@modules/oracle/hooks';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { trackWisdomEvents } from '@modules/oracle/utils/trackWisdomEvents';
import EditRounded from '@mui/icons-material/EditRounded';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography, alpha } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface OracleConversationQuestionProps {
  promptText: string;
  promptID: string;
  isReadOnly?: boolean;
}

export function OracleConversationQuestion({
  promptText,
  promptID,
  isReadOnly,
}: OracleConversationQuestionProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [question, setQuestion] = useState(promptText);
  const askOracleInputRef = useRef<AskOracleInputActionsHandle | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const sessionID = useOracleSearchStore(({ data }) => data.sessionID);

  const threadPosition = useGetPromptPosition(promptID);

  const onSubmit = async () => {
    setIsLoading(true);
    await askOracleInputRef.current?.submit();
  };

  useEffect(() => {
    if (isReadOnly && isEditMode) {
      setIsEditMode(false);
    }
  }, [isEditMode, isReadOnly]);

  const canOpenEditMode = !isReadOnly && !isEditMode;

  return (
    <Stack
      direction="row"
      spacing={1}
      flex={1}
      alignItems="center"
      position="relative"
      sx={{
        '.q-edit-cta': {
          opacity: 0,
        },
        ':hover': {
          '.q-edit-cta': {
            opacity: canOpenEditMode ? 1 : 0,
          },
          cursor: canOpenEditMode ? 'pointer' : undefined,
          ':hover::after': {
            content: '""',
            position: 'absolute',
            top: '-5px',
            left: '-8px',
            right: '-8px',
            bottom: '-5px',
            bgcolor: (theme) =>
              canOpenEditMode
                ? alpha(theme.palette.primary.main, 0.08)
                : undefined,
            borderRadius: 1,
            zIndex: -1,
          },
        },
      }}
      onClick={() => {
        if (!canOpenEditMode) {
          return;
        }
        setIsEditMode(true);
      }}
    >
      {isEditMode ? (
        <Stack spacing={2} width="100%">
          <AskOracleInput
            ref={askOracleInputRef}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
            }}
            editedFromResponseID={promptID}
            components={{
              TextField: {
                InputProps: {
                  endAdornment: null,
                },
                helperText: null,
                placeholder: 'Ask a question...',
              },
            }}
            onFocus={(event) =>
              event.currentTarget?.setSelectionRange(
                event.currentTarget.value.length,
                event.currentTarget.value.length,
              )
            }
            onSubmit={(searchText: string) => {
              setIsLoading(false);
              setIsEditMode(false);
              trackWisdomEvents({
                eventName: 'Wisdom - Input Edited',
                searchText: searchText,
                previousSearchText: promptText,
                sessionID,
                threadPosition,
              });
            }}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setIsEditMode(false);
                setQuestion(promptText);
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              disabled={question === promptText}
              loading={isLoading}
              onClick={onSubmit}
            >
              Submit
            </LoadingButton>
          </Stack>
        </Stack>
      ) : (
        <Typography
          variant="h5"
          color="text.primary"
          sx={{ whiteSpace: 'pre-line' }}
        >
          {question}
        </Typography>
      )}
      <EditRounded
        className="q-edit-cta"
        sx={{
          color: 'text.disabled',
        }}
      />
    </Stack>
  );
}
